import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Modal, Row } from 'react-bootstrap';
import Select from 'react-select';
import { FaPaperclip } from 'react-icons/fa';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  arquivoPermitido,
  buscarOpcaoPorValor,
  montarOpcoesAssuntos,
  montarOpcoesClientes,
  montarOpcoesEmpreendimentos,
  montarOpcoesGruposEmpresa,
  montarOpcoesRazoes,
  prioridadesSolicitacao,
} from './helpers';

const estadoInicial = {
  codigo_ge: null,
  codigo_razao: null,
  codigo_conta: null,
  codigo_familia: null,
  codigo_assunto: null,
  prioridade: null,
  obs: '',
};

const modalNovaStyles = {
  modalBody: {
    backgroundColor: '#ffffff',
    padding: '12px 18px 18px',
  },
  sectionCard: {
    border: '1px solid #dde5ef',
    borderRadius: '12px',
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
    marginBottom: '14px',
  },
  sectionHeader: {
    background: 'linear-gradient(90deg, #f8fafc 0%, #eef4fb 100%)',
    borderBottom: '1px solid #dde5ef',
    padding: '10px 14px',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '14px',
    fontWeight: 700,
    color: '#24324a',
  },
  sectionBody: {
    padding: '14px',
  },
  assuntoBox: {
    border: '1px solid #e5eaf1',
    borderRadius: '10px',
    backgroundColor: '#fbfdff',
    padding: '10px 12px',
    color: '#334155',
    lineHeight: '1.45',
  },
  campoTexto: {
    borderRadius: '6px',
    width: '100%',
    minHeight: '38px',
    height: '38px',
    fontFamily: "'Open Sans', Arial, sans-serif",
    boxSizing: 'border-box',
    border: '1px solid #ced4da',
    backgroundColor: '#fff',
    padding: '0.375rem 0.75rem',
    fontSize: '0.875rem',
    lineHeight: '1.5',
  },
  campoTextarea: {
    borderRadius: '6px',
    width: '100%',
    minHeight: '98px',
    fontFamily: "'Open Sans', Arial, sans-serif",
    boxSizing: 'border-box',
    border: '1px solid #ced4da',
    backgroundColor: '#fff',
    padding: '0.375rem 0.75rem',
    fontSize: '0.875rem',
    lineHeight: '1.5',
  },
  arquivoBox: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    border: '1px dashed #b8c7d9',
    borderRadius: '10px',
    backgroundColor: '#fbfdff',
    padding: '10px',
  },
  arquivoBotao: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #0d6efd',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#0d6efd',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    padding: '7px 12px',
    margin: '0 10px 0 0',
  },
  arquivoNome: {
    color: '#64748b',
    fontSize: '13px',
    lineHeight: '1.35',
  },
};

const SecaoCardNova = ({ titulo, children }) => (
  <Card style={modalNovaStyles.sectionCard}>
    <Card.Header style={modalNovaStyles.sectionHeader}>
      <h6 style={modalNovaStyles.sectionTitle}>{titulo}</h6>
    </Card.Header>
    <Card.Body style={modalNovaStyles.sectionBody}>{children}</Card.Body>
  </Card>
);

const ModalNovaSolicitacao = ({
  show,
  lookups,
  razoes,
  clientes,
  empreendimentos,
  carregandoDependentes,
  salvando,
  onHide,
  onSelecionarGe,
  onSalvar,
}) => {
  const [form, setForm] = useState(estadoInicial);
  const [arquivo, setArquivo] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (show) {
      setForm(estadoInicial);
      setArquivo(null);
      setErro('');
    }
  }, [show]);

  const opcoesGe = montarOpcoesGruposEmpresa(
    lookups && lookups.grupos_empresa ? lookups.grupos_empresa : []
  );
  const opcoesRazoes = montarOpcoesRazoes(razoes || []);
  const opcoesClientes = montarOpcoesClientes(clientes || []);
  const opcoesEmpreendimentos = montarOpcoesEmpreendimentos(empreendimentos || []);
  const opcoesAssuntos = montarOpcoesAssuntos(
    lookups && lookups.assuntos ? lookups.assuntos : []
  );

  const assuntoSelecionado = useMemo(() => {
    const opcao = buscarOpcaoPorValor(opcoesAssuntos, form.codigo_assunto);
    return opcao || null;
  }, [opcoesAssuntos, form.codigo_assunto]);

  const dadosAssunto =
    assuntoSelecionado && assuntoSelecionado.codigo
      ? assuntoSelecionado
      : assuntoSelecionado || {};

  const alterarCampo = (campo, valor) => {
    setForm(prev => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const alterarGe = opcao => {
    const codigoGe = opcao ? opcao.value : null;
    setForm(prev => ({
      ...prev,
      codigo_ge: codigoGe,
      codigo_razao: null,
      codigo_conta: null,
      codigo_familia: null,
    }));
    onSelecionarGe(codigoGe);
  };

  const alterarArquivo = event => {
    const selecionado =
      event.target.files && event.target.files.length > 0
        ? event.target.files[0]
        : null;

    if (selecionado && !arquivoPermitido(selecionado)) {
      setArquivo(null);
      setErro('Anexo inválido. Use PDF, JPG, PNG, DOCX, XLSX ou CSV.');
      return;
    }

    setErro('');
    setArquivo(selecionado);
  };

  const validar = () => {
    if (!form.codigo_ge) return 'Informe o GE.';
    if (!form.codigo_razao) return 'Informe a razão social.';
    if (!form.codigo_conta) return 'Informe o cliente.';
    if (!form.codigo_familia) return 'Informe o empreendimento.';
    if (!form.codigo_assunto) return 'Informe o assunto.';
    if (form.prioridade === null || form.prioridade === undefined || form.prioridade === '') {
      return 'Informe a prioridade.';
    }
    return '';
  };

  const salvar = () => {
    const mensagem = validar();
    if (mensagem) {
      setErro(mensagem);
      return;
    }

    setErro('');
    onSalvar(
      {
        codigo_ge: form.codigo_ge,
        codigo_razao: form.codigo_razao,
        codigo_conta: form.codigo_conta,
        codigo_familia: form.codigo_familia,
        codigo_assunto: form.codigo_assunto,
        prioridade: form.prioridade,
        obs: form.obs,
      },
      arquivo
    );
  };

  const renderSelect = ({
    label,
    campo,
    opcoes,
    placeholder,
    disabled,
    loading,
    onChange,
  }) => (
    <Col xs={12} md={6} className="mb-2">
      <Form.Label className="small mb-1">{label}</Form.Label>
      <Select
        isClearable
        isDisabled={disabled}
        isLoading={loading}
        placeholder={placeholder || 'Selecione...'}
        options={opcoes}
        value={buscarOpcaoPorValor(opcoes, form[campo])}
        onChange={onChange || (opcao => alterarCampo(campo, opcao ? opcao.value : null))}
        components={{ DropdownIndicator }}
        styles={customStyles}
        noOptionsMessage={() => 'Nenhuma opção encontrada'}
      />
    </Col>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Nova Solicitação</Modal.Title>
      </Modal.Header>
      <Modal.Body style={modalNovaStyles.modalBody}>
        {erro && <Alert variant="danger">{erro}</Alert>}

        <SecaoCardNova titulo="Dados da solicitação">
          <Row>
            {renderSelect({
              label: 'GE',
              campo: 'codigo_ge',
              opcoes: opcoesGe,
              onChange: alterarGe,
            })}
            {renderSelect({
              label: 'Razão social',
              campo: 'codigo_razao',
              opcoes: opcoesRazoes,
              disabled: !form.codigo_ge,
              loading: carregandoDependentes,
            })}
          </Row>
          <Row>
            {renderSelect({
              label: 'Cliente',
              campo: 'codigo_conta',
              opcoes: opcoesClientes,
              disabled: !form.codigo_ge,
              loading: carregandoDependentes,
            })}
            {renderSelect({
              label: 'Empreendimento',
              campo: 'codigo_familia',
              opcoes: opcoesEmpreendimentos,
              disabled: !form.codigo_ge,
              loading: carregandoDependentes,
            })}
          </Row>
          <Row>
            {renderSelect({
              label: 'Prioridade',
              campo: 'prioridade',
              opcoes: prioridadesSolicitacao,
            })}
            {renderSelect({
              label: 'Assunto',
              campo: 'codigo_assunto',
              opcoes: opcoesAssuntos,
            })}
          </Row>
        </SecaoCardNova>

        {form.codigo_assunto && (
          <SecaoCardNova titulo="Contexto do assunto">
          <div className="small" style={modalNovaStyles.assuntoBox}>
            <div>
              <strong>Departamento:</strong>{' '}
              {dadosAssunto.departamento_descricao || ''}
            </div>
            <div>
              <strong>Descrição:</strong> {dadosAssunto.descricao || ''}
            </div>
          </div>
          </SecaoCardNova>
        )}

        <SecaoCardNova titulo="Anexo e observação">
          <Form.Group>
            <Form.Label className="small mb-1">Anexo</Form.Label>
            <div style={modalNovaStyles.arquivoBox}>
              <input
                id="arquivo-nova-solicitacao-juridica"
                type="file"
                accept=".pdf,.jpg,.png,.docx,.xlsx,.csv"
                style={{ display: 'none' }}
                onChange={alterarArquivo}
              />
              <label
                htmlFor="arquivo-nova-solicitacao-juridica"
                style={modalNovaStyles.arquivoBotao}
              >
                <FaPaperclip className="mr-1" /> Selecionar arquivo
              </label>
              <span style={modalNovaStyles.arquivoNome}>
                {arquivo && arquivo.name ? arquivo.name : 'Nenhum arquivo selecionado'}
              </span>
            </div>
          </Form.Group>

          <Form.Group className="mb-0">
            <Form.Label className="small mb-1">Observação</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={form.obs}
              style={modalNovaStyles.campoTextarea}
              onChange={event => alterarCampo('obs', event.target.value)}
            />
          </Form.Group>
        </SecaoCardNova>
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm" variant="primary" onClick={salvar} disabled={salvando}>
          Salvar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalNovaSolicitacao;
